import {z} from "zod";
import {useTranslation} from "react-i18next";
import {useAtom, useAtomValue} from "jotai";
import {useMemo} from "react";
import {zodResolver} from "@hookform/resolvers/zod";
import {UseFormReturn, useForm} from "react-hook-form";
import dayjs from "dayjs";
import {Grid, Typography} from "@mui/material";
import {swallowException} from "../../utils/appUtils";
import {getResourceErrorMessage} from "../../utils/resourceUtils";
import {Dialog} from "../base/Dialog";
import {LabeledInput} from "../base/LabeledInput";
import {User} from "../../types/domain/user";
import {CompanyMemberRole} from "../../types/domain/companyMember";
import {useResourceManagement, useResourceValueOrDefault} from "../../hooks/resourceHooks";
import {SelectInput} from "../base/SelectInput";
import {
    createCompanyMember,
    patchCompanyMember,
    deleteCompanyMember,
    resetPasswordCompanyMember,
} from "../../services/companyMemberService";
import {AutocompleteInput} from "../base/AutocompleteInput";
import {usersLoadable} from "../../store/usersStore";
import {getUserFullName} from "../../utils/userUtils";
import {CompanyMemberEx} from "../../types/domain/companyMember";
import {UserIcon} from "../icons/UserIcon";
import {UserInfoForm, UserInfoFormInputs} from "../users/UserInfoForm";
import {patchUser} from "../../services/userService";
import {Frame} from "../base/Frame";
import {formatShortDate} from "../../utils/dateUtils";
import {ActionButton} from "../base/ActionButton";
import {impersonationAtom} from "../../store/impersonationStore";
import {authenticatedUserLoadable} from "../../store/authenticatedUserStore";
import {useUserPreferences} from "../../hooks/userPreferencesHooks";
import {TFunction} from "i18next";

export interface Props {
    mode: "create" | "edit";
    companyId: string;
    member?: CompanyMemberEx;
    user?: User;
    managerRoleEnabled: boolean;
    open: boolean;
    onClose: (isSuccessful: boolean) => void;
}

export const CompanyMemberEditDialog: React.FC<Props> = (props) => {
    const {t} = useTranslation();
    const {dateFormat} = useUserPreferences();
    const {mode, member, user, companyId, open, onClose, managerRoleEnabled} = props;
    const userManagement = useResourceManagement({patchUser});
    const usersState = useAtomValue(usersLoadable);
    const [impersonation, setImpersonation] = useAtom(impersonationAtom);
    const authenticatedUserResource = useAtomValue(authenticatedUserLoadable);
    const authenticatedUser = useResourceValueOrDefault(authenticatedUserResource, undefined);
    const memberManagement = useResourceManagement({
        createCompanyMember,
        patchCompanyMember,
        deleteCompanyMember,
        resetPasswordCompanyMember,
    });
    const initFormValues = mode === "edit" ? memberToFormInputs(member!, user!) : DEFAULT_FORM_INPUTS;
    const form = useForm({
        defaultValues: initFormValues,
        resolver: zodResolver(MEMBER_INVITE_SCHEMA),
    });
    const roleOptions = useMemo(() => getRoleOptions(managerRoleEnabled, t), [managerRoleEnabled, t]);

    const onSubmit = (inputs: FormInputs) => {
        memberManagement.reset();
        userManagement.reset();
        if (mode === "create") {
            memberManagement
                .createCompanyMember(companyId, {
                    userId: inputs.userId,
                    role: inputs.role,
                    attributes: {},
                })
                .then(() => onClose(true))
                .catch(swallowException);
        } else {
            memberManagement
                .patchCompanyMember(companyId, inputs.userId, {
                    role: inputs.role,
                })
                .then(() => onClose(true))
                .catch(swallowException);
        }
    };

    const onDelete = () => {
        memberManagement.reset();

        memberManagement
            .deleteCompanyMember(companyId, member!.userId)
            .then(() => onClose(true))
            .catch(swallowException);
    };

    const onResetPassword = () => {
        memberManagement.reset();

        memberManagement
            .resetPasswordCompanyMember(companyId, member!.userId)
            .then(() => onClose(true))
            .catch(swallowException);
    };

    const onImpersonate = () => {
        setImpersonation(member!.userId);
        onClose(true);
    };

    return (
        <Dialog
            width="50rem"
            enableSubmit
            enableDelete={mode === "edit"}
            deleteTitle={t("dialogs.actions.delete")}
            submitTitle={t("dialogs.actions.save")}
            title={mode === "edit" ? t("dialogs.member_edit.edit_title") : t("dialogs.member_edit.create_title")}
            titleIcon={
                <UserIcon
                    sx={{
                        stroke: "#404DB8",
                        width: "1.125rem",
                        height: "1.125rem",
                    }}
                />
            }
            open={open}
            error={getResourceErrorMessage(memberManagement.resource)}
            deleteConfirmation={t("dialogs.member_edit.delete_confirmation")}
            onClose={() => onClose(false)}
            onDelete={onDelete}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(onSubmit)}
            headerContent={
                <ActionButton
                    id="impersonate"
                    icon={"group"}
                    color={"primary"}
                    onClick={props.mode === "edit" ? onImpersonate : undefined}
                    disabled={impersonation !== undefined || member?.userId === authenticatedUser?.id}
                    variant="text"
                    sx={{paddingY: "0.5rem", paddingX: "1.5rem", marginY: "-0.5rem"}}
                >
                    {t("dialogs.member_edit.impersonate")}
                </ActionButton>
            }
        >
            <Grid container spacing={2}>
                {mode === "edit" && (
                    <Grid item xs={12}>
                        <UserInfoForm
                            initFormValues={initFormValues}
                            form={form as unknown as UseFormReturn<UserInfoFormInputs>}
                            disabled={true}
                        />
                    </Grid>
                )}
                {mode === "create" && (
                    <Grid item xs={12}>
                        <Frame>
                            <LabeledInput label={t("dialogs.member_edit.user")}>
                                <AutocompleteInput
                                    name="userId"
                                    control={form.control}
                                    options={usersState.state === "hasData" ? usersState.data : []}
                                    isLoading={usersState.state === "loading"}
                                    initialValue={initFormValues.userId}
                                    getOptionLabel={getUserLabel}
                                />
                            </LabeledInput>
                        </Frame>
                    </Grid>
                )}
                <Grid item xs={12}>
                    <Frame>
                        <Typography fontSize={16} textTransform="uppercase" sx={{mb: "1rem"}}>
                            {t("dialogs.member_edit.account")}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <LabeledInput label={t("dialogs.member_edit.role")}>
                                    <SelectInput
                                        name="role"
                                        control={form.control}
                                        options={roleOptions}
                                        initialValue={initFormValues.role}
                                        getOptionLabel={(option) => option.label}
                                    />
                                </LabeledInput>
                            </Grid>
                            {mode === "edit" && (
                                <>
                                    <Grid item xs={12}>
                                        <LabeledInput label={t("dialogs.member_edit.last_login")}>
                                            <Typography fontSize={16} textTransform="uppercase" sx={{mb: "1rem"}}>
                                                {member?.lastActivity &&
                                                    formatShortDate(dayjs(member.lastActivity), dateFormat)}
                                            </Typography>
                                        </LabeledInput>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <ActionButton
                                            id="reset-pwd"
                                            icon={"back"}
                                            color={"secondary"}
                                            onClick={onResetPassword}
                                        >
                                            {t("dialogs.member_edit.reset_pwd")}
                                        </ActionButton>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Frame>
                </Grid>
            </Grid>
        </Dialog>
    );
};

const getUserLabel = (user: User) => `${getUserFullName(user)} <${user.properties.email}>`;

interface FormInputs extends UserInfoFormInputs {
    userId: string;
    role: CompanyMemberRole;
}

const MEMBER_INVITE_SCHEMA = z.object({
    userId: z.string(),
    role: z.nativeEnum(CompanyMemberRole),
    firstName: z.string().max(200),
    lastName: z.string().max(200),
    email: z.string(),
    company: z.string(),
    country: z.string().nullable(),
    phone: z.string().max(200),
});

const DEFAULT_FORM_INPUTS: FormInputs = {
    userId: "",
    role: CompanyMemberRole.VIEWER,
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    country: null,
    phone: "",
};

const memberToFormInputs = (member: CompanyMemberEx, user: User): FormInputs => ({
    userId: member.userId,
    role: member.role,
    ...userToFormInputs(user),
});

const getRoleOptions = (managerRoleEnabled: boolean, t: TFunction) =>
    Object.values(CompanyMemberRole)
        .filter((role) => managerRoleEnabled || role !== CompanyMemberRole.MANAGER)
        .map((role) => ({
            id: role,
            label: t(`enumerations.company_roles.${role.toLowerCase()}`),
        }));

const userToFormInputs = (user: User): UserInfoFormInputs => ({
    firstName: user.properties.firstName,
    lastName: user.properties.lastName,
    email: user.properties.email,
    company: user.properties.company ?? "",
    country: user.properties.country ?? "",
    phone: user.properties.phone ?? "",
});
